/**
 * Kassaldo Store — wat hoort er fysiek in de kas te liggen?
 *
 * Gedeeld door /kas en /financieel (Cash Positie), zodat beide schermen
 * hetzelfde getal tonen en er maar één fetch-pad is.
 *
 * Rekenmodel: `api/_lib/finance/kas-saldo.ts`.
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';

export interface KasStorting {
  id: string;
  stortingDatum: string;
  bedrag: number;
  bron: 'bank' | 'handmatig';
  omschrijving: string | null;
}

export interface AnkerControle {
  houdbaar: boolean;
  diepsteDatum: string | null;
  diepsteSaldo: number;
  minimaalAnker: number;
  tekort: number;
}

export interface KasSaldo {
  heeftAnker: boolean;
  ankerSoort: 'telling' | 'aanname' | null;
  anker: { datum: string; bedrag: number; opmerking: string | null } | null;
  controle: AnkerControle;
  vanaf: string;
  contantOntvangen: number;
  gestort: number;
  saldo: number;
  nogNietAfgetekend: number;
  inKluis: number;
  laatsteStorting: { datum: string; bedrag: number } | null;
  dagenSindsStorting: number | null;
  contantSindsStorting: number;
  stortingen: KasStorting[];
  nieuweStortingenGevonden: number;
}

interface KasSaldoState {
  saldo: KasSaldo | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  laadSaldo: () => Promise<void>;
  legTellingVast: (
    teldatum: string,
    getelBedrag: number,
    opmerking?: string,
    soort?: 'telling' | 'aanname'
  ) => Promise<number | null | 'fout'>;
  legStortingVast: (stortingDatum: string, bedrag: number, omschrijving?: string) => Promise<boolean>;
}

async function authHeaders(): Promise<HeadersInit | null> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return null;
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

/**
 * De kas staat twee keer op /financieel (als bron in Cash Positie en als
 * beheerkaart), en beide monteren tegelijk. Zonder deze guard vuurt dat twee
 * identieke requests af waarvan de traagste de snelste overschrijft.
 */
let lopendeFetch: Promise<void> | null = null;

export const useKasSaldoStore = create<KasSaldoState>()((set) => ({
  saldo: null,
  isLoading: false,
  isSaving: false,
  error: null,

  laadSaldo: async () => {
    if (lopendeFetch) return lopendeFetch;
    set({ isLoading: true, error: null });
    lopendeFetch = (async () => {
      try {
        const headers = await authHeaders();
        if (!headers) throw new Error('Niet ingelogd');
        const r = await fetch('/api/kas-saldo', { headers });
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error || `Kassaldo laden mislukt (${r.status})`);
        }
        set({ saldo: (await r.json()) as KasSaldo, isLoading: false });
      } catch (e) {
        set({ error: e instanceof Error ? e.message : 'Kassaldo laden mislukt', isLoading: false });
      } finally {
        lopendeFetch = null;
      }
    })();
    return lopendeFetch;
  },

  /** Returnt het verschil met wat het systeem verwachtte, of 'fout'. */
  legTellingVast: async (teldatum, getelBedrag, opmerking, soort = 'telling') => {
    set({ isSaving: true, error: null });
    try {
      const headers = await authHeaders();
      if (!headers) throw new Error('Niet ingelogd');
      const r = await fetch('/api/kas-saldo?action=telling', {
        method: 'POST',
        headers,
        body: JSON.stringify({ teldatum, getelBedrag, opmerking, soort }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `Telling opslaan mislukt (${r.status})`);
      set({ saldo: body.saldo as KasSaldo, isSaving: false });
      return body.verschilMetVerwacht as number | null;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Telling opslaan mislukt', isSaving: false });
      return 'fout';
    }
  },

  legStortingVast: async (stortingDatum, bedrag, omschrijving) => {
    set({ isSaving: true, error: null });
    try {
      const headers = await authHeaders();
      if (!headers) throw new Error('Niet ingelogd');
      const r = await fetch('/api/kas-saldo?action=storting', {
        method: 'POST',
        headers,
        body: JSON.stringify({ stortingDatum, bedrag, omschrijving }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.error || `Storting opslaan mislukt (${r.status})`);
      set({ saldo: body.saldo as KasSaldo, isSaving: false });
      return true;
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Storting opslaan mislukt', isSaving: false });
      return false;
    }
  },
}));

/** Losse selector zodat tegels niet op de hele store abonneren. */
export const selectKasSaldoBedrag = (s: KasSaldoState) => s.saldo?.saldo ?? null;
